import {ref, computed, inject, provide, type Ref, type ComputedRef, type InjectionKey, onUnmounted, watch} from "vue";

/**
 * PanelTree is helper class intended to manage parent-child hierarchy of opened panels and windows in DOM.
 * It is difficult to manage focus and mouse interaction across multiple panels.
 *
 * This utility uses anchor - boat terminology to connect parent - child elements.
 * Parent element (which is anchor) can spawn a new child (boat). This library makes
 * sure that the boat is always connected to the anchor even when scrolling the page
 * or changing dimensions of both anchor and boat.
 * The boat can contain other anchors and those can spawn other boats. This can create
 * unlimited chain of managed elements in the DOM.
 * The first anchor in the chain is called "master anchor".
 * Basic rules:
 *  - If user clicks outside the chain all boats are released (removed from the DOM).
 *  - If anchor moves or change its dimensions boat is repositioned
 *  - If an anchor is removed all connected boats are released.
 *
 * How to use this utility:
 * Let's suppose you have DOM element which should open a context menu on mouse click.
 * If the user clicks on the element a new child element is created
 */
import {ElementMeasure, getElementMeasure} from "@fd/helpers/DomUtils";


/**
 * There is 12 possible placements of tail of boat bubble.
 * The following position naming is in the format <edge>-<placement>
 *
 *               top-left          top-center          top-right
 * 		        +-----------------------------------------------+
 * 	   left-top |                                               | right-top
 * 	left-center |                                               | right-center
 * 	left-bottom |                                               | right-bottom
 * 		        +-----------------------------------------------+
 *               bottom-left      bottom-center     bottom-right
 */
export type TailPlacement = "none" | "top-left" | "top-center" | "top-right"
	| "bottom-left" | "bottom-center" | "bottom-right"
	| "left-top" | "left-center" | "left-bottom"
	| "right-top" | "right-center" | "right-bottom";


export interface ChainPlacement {
	/** horizontal anchor number (typically between 0..100 where 0 means left edge and 100 right edge) */
	ah: number;
	/** vertical anchor number (typically between 0..100 where 0 means top edge and 100 bottom edge) */
	av: number;
	/** horizontal boat number */
	bh: number;
	/** vertical boat number */
	bv: number;
	/** horizontal boat offset in pixels */
	x: number;
	/** vertical boat offset in pixels */
	y: number;

	/** Maximum number of pixels available between anchor edge and viewport edge */
	spaceLimit?:number;

	/**
	 * Speech bubble tail placement
	 **/
	tail?: TailPlacement;
}

/** Distance of the tail tip from nearest corner (should be at least half of tail width + box radius) */
export const TAIL_EDGE_DIST = 16;

export interface ChainOffset {
	/** horizontal offset in pixels */
	x: number;
	/** vertical offset in pixels */
	y: number;
}

export type ChainPlacementFunction = (ElementMeasure) => ChainPlacement;
export type ChainRule = ChainPlacement | ChainPlacementFunction;

/** Drop-down placement (under the anchor) */
export const DROPDOWN_PLACEMENT:ChainPlacement = {
	ah: 0, av: 100,
	bh: 0, bv: 0,
	x: 0, y: 0
};

export const DROPUP_PLACEMENT:ChainPlacement = {
	ah: 0, av: 0,
	bh: 0, bv: 100,
	x: 0, y: 0
};

export const RIGHT_PLACEMENT:ChainPlacement = {
	ah: 100, av: 50,
	bh: 0, bv: 50,
	x: 0, y: 0
};

export const LEFT_PLACEMENT:ChainPlacement = {
	ah: 0, av: 50,
	bh: 100, bv: 50,
	x: 0, y: 0
};


export interface ChainOptions {
	/** If true the boat is visible and all coordinates are updated */
	visible: boolean;
	/**
	 * Set this option to true if visiblity of this panel is fully managed by owning component.
	 * Managed chains cannot be closed by other components or by clicking body.
	 **/
	managed: boolean;
	rule: ChainRule;
}

/**
 * Calculated point
 */
export interface ChainEnd {
	x: number;
	y: number;
	/* If true x and y are relative to viewport, if false both are relative to the document */
	fixed: boolean;
	/* Rule used to calculate the point */
	rule?: ChainPlacement;
}

export interface Chain {
	uid: string;
	parent: Chain | null;
	children: Set<Chain>;

	visible: Ref<boolean>;
	managed: Ref<boolean>;

	/** True if this one boat has mouse */
	mouseInBout: Ref<boolean>;
	/** True if this boat or any of its sub-boats has mouse */
	mouseIn: Ref<boolean>;

	/** True if this one boat has focus */
	focusInBout: Ref<boolean>;
	/** True if this boat or any of its sub-boats has focus */
	focusIn: Ref<boolean>;

	/** True if user is just interacting with boat UI */
	interacting: Ref<boolean>;

	/** Coordinates of boat connection point ... updated if visible = true */
	x: ComputedRef<number>;
	y: ComputedRef<number>;
	/* If true coordinates are relative to viewport, if false coordinates are relative to the document */
	fixed: ComputedRef<boolean>;
	/* Latest anchor measurement */
	anchor: Ref<ElementMeasure|null>;
	/** Current placement */
	rule: ComputedRef<ChainPlacement|null>;

	anchorElement: Ref<HTMLElement|null>;
	boatElement: Ref<HTMLElement|null>;

	update: () => void;
	close: () => void;
	closeChildren: () => void;

    show: () => void;
    hide: () => void;
	/** Toggle menu and return the new state */
    toggle: () => boolean;
}

export interface ChainContext {
	closePanels: (force:boolean, ...except:string[]) => void
}

const chainKey = Symbol() as InjectionKey<Chain>;

/** All panel hierarchies starts here */
const pageFloor = new Set<Chain>();

function parentChain():Chain|null {
	return inject(chainKey, null);
}

function provideNewParent(ch:Chain) {
	return provide(chainKey, ch);
}

/**
 * Test if the element is positioned as fixed or one of its parents is fixed
 */
export function isFixedPosition(element:HTMLElement):boolean {
	while (element && element.tagName !== 'BODY') {
		if (window.getComputedStyle(element).getPropertyValue('position').toLowerCase() === 'fixed') return true;
		element = element.parentElement;
	}
	return false;
}

export function calcChainEnd(e:HTMLElement|null, r:ChainPlacement):ChainEnd {
	if (e == null) return { x: 0, y: 0, fixed: true, rule: r };	// default
	const fixed = isFixedPosition(e);
	const rect = e.getBoundingClientRect();
	let x = rect.x;
	let y = rect.y;
	if (!fixed) {
		x = x + window.scrollX;
		y = y + window.scrollY;
	}
	if (r.ah !== 0) {
		x += rect.width * (r.ah/100);
	}
	if (r.av !== 0) {
		y += rect.height * (r.av/100);
	}

	return { x, y, fixed, rule: r };
}

const UNKNOWN_CHAIN_END:ChainEnd = { x: 0, y: -10000, fixed: false } as const;

function selectPlacement(measure:ElementMeasure, rule:ChainRule):ChainPlacement {
	if (rule) {
		if (typeof rule === 'function') {
			// Dynamic placement
			return rule(measure);
		} else return rule;		// Static placement
	} else {
		// Default placement
		return DROPDOWN_PLACEMENT;
	}
}

function floorDump(floor: Set<Chain>) {
	const ids:string[] = [];
	floor.forEach((item) => {
		if (item.visible.value) ids.push(item.uid);
	});
	return "[" + ids.join(", ") + "]";
}

/** Collapse al panels on the floor except those with give IDs. */
function closeAllSiblings(floor: Set<Chain>, force:boolean = false, ...except:string[]):void {
	//console.log(" - Close siblings: floor=" + floorDump(floor) + " with exceptions " + except.join("/"));
	for (let child of floor) {
		if (!force && child.managed.value) continue;
		if (!child.uid || except.indexOf(child.uid) < 0) {
			child.close();
		}
	}
	//console.log(" - After the close: floor=" + floorDump(floor));
}

export function createChain(ops: Partial<ChainOptions>, uid:string = ""):Chain {
	const reactiveForceRecalc = ref<number>(1);
	const anchorElement = ref<HTMLElement | null>(null);
	const boatElement = ref<HTMLElement | null>(null);

	const visible = ref<boolean>(!!ops.visible);
	const managed = ref<boolean>(!!ops.managed);
	const mouseInBout = ref<boolean>(false);
	const focusInBout = ref<boolean>(false);
	const anchor = ref<ElementMeasure | null>(null);

	const point = computed<ChainEnd>(() => {
		if (visible.value) {
			if (anchorElement.value && boatElement.value && reactiveForceRecalc.value>0) {
				// Select rule
				const measure: ElementMeasure = getElementMeasure(anchorElement.value);
				anchor.value = measure;
				const r: ChainPlacement = selectPlacement(measure, ops.rule);
				const ce = calcChainEnd(anchorElement.value, r);
				if (typeof r.x === 'number') ce.x += r.x;
				if (typeof r.y === 'number') ce.y += r.y;
				return ce;
			} else anchor.value = null;
		}
		return UNKNOWN_CHAIN_END;
	});

	const x = computed<number>(() => point.value.x);
	const y = computed<number>(() => point.value.y);
	const fixed = computed<boolean>(() => point.value.fixed);
	const rule = computed<ChainPlacement>(() => point.value.rule);

	const parent = parentChain();
	const children = new Set<Chain>();

	// Register to parent, No parent => register to page
	const registry:Set<Chain> = parent ? parent.children : pageFloor;

	const mouseIn = computed<boolean>(() => {
		if (mouseInBout.value) return true;
		for (let child of children.values()) {
			if (child.mouseIn.value) return true;
		}
		return false;
	});

	const focusIn = computed<boolean>(() => {
		if (focusInBout.value) return true;
		for (let child of children.values()) {
			if (child.focusIn.value) return true;
		}
		return false;
	});

	const interacting = computed<boolean>(() => {
		return mouseIn.value || focusIn.value;
	});

	const ch:Chain = {
		uid,
		visible,
		managed,
		mouseInBout,
		mouseIn,
		focusInBout,
		focusIn,
		interacting,
		rule,
		x, y,
		fixed,
		anchor,

		anchorElement,
		boatElement,
		parent,
		children,

		update: () => {
			reactiveForceRecalc.value++;
			for (let child of children) {
				child.update();
			}
		},

		close: () => {
			for (let child of children) {
				child.close();
			}
			visible.value = false;
			mouseInBout.value = false;	// closed has no mouse nor the focus
			focusInBout.value = false;
		},

		closeChildren: () => {
			for (let child of children) {
				child.close();
			}
		},

        show: () => {
			if (!visible.value) closeAllSiblings(registry);
			visible.value = true;
		},
        hide: () => {
			visible.value = false;
			mouseInBout.value = false;	// closed has no mouse nor the focus
			focusInBout.value = false;
		},
        toggle: () => {
			if (!visible.value) closeAllSiblings(registry);
            return (visible.value = !visible.value);
        }
	};
	registry.add(ch);
	// The chain must be removed from the parent on unmount
	onUnmounted(() => {
		registry.delete(ch);
		ch.parent = null;
	});

	provideNewParent(ch);
	return ch;
}


/** Collapse al panels on the page */
export function closeAll():void {
	closeAllSiblings(pageFloor);
}

/**
 * This is for components which don't create chain but need to interact with panel tree.
 * E.g. need to close all sub-panels.
 */
export function chainContext():ChainContext {
	const parent = parentChain();
	// Register to parent, No parent => register to page
	const registry:Set<Chain> = parent ? parent.children : pageFloor;

	return {
		closePanels(force:boolean  = false, ...except:string[]) {
			closeAllSiblings(registry, force, ...except);
		}
	}
}

function updateBoatPositions():void {
	//console.log("Update called " + pageFloor.size);
	for (let chain of pageFloor.values()) {
		chain.update();
	}
}


window.onload = () => {
	/* Click on the body will always close all chains */
	document.body.addEventListener("mousedown", () => {
		//console.log("body.mousedown");
		closeAll();
	});

	window.addEventListener("resize", updateBoatPositions);
	window.addEventListener("scroll", updateBoatPositions);
}
